import React, { useState, useEffect, useMemo } from 'react';
import { Eraser, Copy, Download, Shuffle, Eye, Settings, Hash, Filter, Minimize2, Trash2 } from 'lucide-react';

interface RemovalMethod {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

interface RemovalOptions {
  preserveLineBreaks: boolean;
  preserveIndentation: boolean;
  preserveSingleSpaces: boolean;
  convertTabsToSpaces: boolean;
  spacesPerTab: number;
  removeLeadingWhitespace: boolean;
  removeTrailingWhitespace: boolean;
  removeExtraSpaces: boolean;
  removeEmptyLines: boolean;
  normalizeLineEndings: boolean;
  trimEachLine: boolean;
  preserveStructure: boolean;
  removeAllWhitespace: boolean;
  replaceWhitespaceWith: string;
  minimumLineLength: number;
  maximumLineLength: number;
}

interface RemovalResult {
  originalText: string;
  processedText: string;
  statistics: WhitespaceStats;
}

interface WhitespaceStats {
  originalLength: number;
  processedLength: number;
  charactersRemoved: number;
  spacesRemoved: number;
  tabsRemoved: number;
  newlinesRemoved: number;
  originalLines: number;
  processedLines: number;
  emptyLinesRemoved: number;
  reductionPercentage: number;
  whitespaceTypes: {
    spaces: number;
    tabs: number;
    newlines: number;
    carriageReturns: number;
    formFeeds: number;
    verticalTabs: number;
  };
}

const WhitespaceRemover: React.FC = () => {
  const [inputText, setInputText] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('basic');
  const [outputText, setOutputText] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showStats, setShowStats] = useState(false);

  const [options, setOptions] = useState<RemovalOptions>({
    preserveLineBreaks: true,
    preserveIndentation: false,
    preserveSingleSpaces: true,
    convertTabsToSpaces: false,
    spacesPerTab: 4,
    removeLeadingWhitespace: false,
    removeTrailingWhitespace: true,
    removeExtraSpaces: true,
    removeEmptyLines: false,
    normalizeLineEndings: false,
    trimEachLine: false,
    preserveStructure: true,
    removeAllWhitespace: false,
    replaceWhitespaceWith: '',
    minimumLineLength: 0,
    maximumLineLength: 1000,
  });

  const removalMethods: RemovalMethod[] = [
    {
      id: 'basic',
      name: 'Basic Cleanup',
      description: 'Remove extra spaces and trailing whitespace',
      icon: <Eraser className="w-4 h-4" />,
      color: 'blue'
    },
    {
      id: 'aggressive',
      name: 'Aggressive Removal',
      description: 'Remove all unnecessary whitespace',
      icon: <Trash2 className="w-4 h-4" />,
      color: 'red'
    },
    {
      id: 'minimal',
      name: 'Minimal Cleanup',
      description: 'Light cleanup preserving structure',
      icon: <Filter className="w-4 h-4" />,
      color: 'green'
    },
    {
      id: 'normalize',
      name: 'Normalize Whitespace',
      description: 'Standardize all whitespace characters',
      icon: <Hash className="w-4 h-4" />,
      color: 'purple'
    },
    {
      id: 'compact',
      name: 'Compact Text',
      description: 'Maximum compression while readable',
      icon: <Minimize2 className="w-4 h-4" />,
      color: 'yellow'
    },
    {
      id: 'trimLines',
      name: 'Trim Lines',
      description: 'Remove leading/trailing spaces per line',
      icon: <Filter className="w-4 h-4" />,
      color: 'indigo'
    },
    {
      id: 'removeEmpty',
      name: 'Remove Empty Lines',
      description: 'Delete all blank lines',
      icon: <Trash2 className="w-4 h-4" />,
      color: 'pink'
    },
    {
      id: 'singleSpace',
      name: 'Single Space',
      description: 'Replace multiple spaces with one',
      icon: <Hash className="w-4 h-4" />,
      color: 'orange'
    },
    {
      id: 'tabsToSpaces',
      name: 'Tabs to Spaces',
      description: 'Convert all tabs to spaces',
      icon: <Filter className="w-4 h-4" />,
      color: 'teal'
    },
    {
      id: 'spacesToTabs',
      name: 'Spaces to Tabs',
      description: 'Convert spaces to tabs for indentation',
      icon: <Hash className="w-4 h-4" />,
      color: 'cyan'
    },
    {
      id: 'removeAll',
      name: 'Remove All Whitespace',
      description: 'Strip every whitespace character',
      icon: <Trash2 className="w-4 h-4" />,
      color: 'emerald'
    },
    {
      id: 'custom',
      name: 'Custom Processing',
      description: 'Use custom options configuration',
      icon: <Settings className="w-4 h-4" />,
      color: 'violet'
    }
  ];

  const processWhitespace = (text: string, method: string, opts: RemovalOptions): RemovalResult => {
    if (!text) {
      return {
        originalText: text,
        processedText: text,
        statistics: createEmptyStats()
      };
    }

    let processed = text;

    switch (method) {
      case 'basic':
        processed = performBasicCleanup(text, opts);
        break;
      case 'aggressive':
        processed = performAggressiveRemoval(text, opts);
        break;
      case 'minimal':
        processed = performMinimalCleanup(text, opts);
        break;
      case 'normalize':
        processed = performNormalization(text, opts);
        break;
      case 'compact':
        processed = performCompaction(text, opts);
        break;
      case 'trimLines':
        processed = performLineTrimming(text, opts);
        break;
      case 'removeEmpty':
        processed = performEmptyLineRemoval(text, opts);
        break;
      case 'singleSpace':
        processed = performSingleSpacing(text, opts);
        break;
      case 'tabsToSpaces':
        processed = performTabsToSpaces(text, opts);
        break;
      case 'spacesToTabs':
        processed = performSpacesToTabs(text, opts);
        break;
      case 'removeAll':
        processed = performCompleteRemoval(text, opts);
        break;
      case 'custom':
        processed = performCustomProcessing(text, opts);
        break;
      default:
        processed = performBasicCleanup(text, opts);
    }

    const statistics = calculateStatistics(text, processed);

    return {
      originalText: text,
      processedText: processed,
      statistics
    };
  };

  const performBasicCleanup = (text: string, opts: RemovalOptions): string => {
    let result = text;
    
    // Remove trailing whitespace from each line
    result = result.split('\n').map(line => line.trimEnd()).join('\n');
    
    // Replace multiple spaces with single space
    result = result.replace(/ {2,}/g, ' ');
    
    // Remove empty lines at the end
    result = result.replace(/\n+$/, '');
    
    return result;
  };

  const performAggressiveRemoval = (text: string, opts: RemovalOptions): string => {
    let result = text;
    
    // Remove all leading and trailing whitespace from lines
    result = result.split('\n').map(line => line.trim()).join('\n');
    
    // Remove empty lines
    result = result.split('\n').filter(line => line.trim() !== '').join('\n');
    
    // Replace multiple spaces with single space
    result = result.replace(/\s+/g, ' ');
    
    return result.trim();
  };

  const performMinimalCleanup = (text: string, opts: RemovalOptions): string => {
    let result = text;
    
    // Only remove trailing spaces
    result = result.split('\n').map(line => line.trimEnd()).join('\n');
    
    return result;
  };

  const performNormalization = (text: string, opts: RemovalOptions): string => {
    let result = text;
    
    // Normalize line endings to \n
    result = result.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    
    // Convert tabs to spaces
    result = result.replace(/\t/g, ' '.repeat(opts.spacesPerTab));
    
    // Normalize multiple spaces to single space (except at line start)
    result = result.split('\n').map(line => {
      const leadingSpaces = line.match(/^ */)?.[0] || '';
      const content = line.trimStart();
      const normalizedContent = content.replace(/ {2,}/g, ' ');
      return leadingSpaces + normalizedContent;
    }).join('\n');
    
    return result;
  };

  const performCompaction = (text: string, opts: RemovalOptions): string => {
    let result = text;
    
    // Trim each line
    result = result.split('\n').map(line => line.trim()).join('\n');
    
    // Remove empty lines
    result = result.split('\n').filter(line => line !== '').join('\n');
    
    // Replace multiple whitespace with single space
    result = result.replace(/\s+/g, ' ');
    
    return result.trim();
  };

  const performLineTrimming = (text: string, opts: RemovalOptions): string => {
    return text.split('\n').map(line => line.trim()).join('\n');
  };

  const performEmptyLineRemoval = (text: string, opts: RemovalOptions): string => {
    return text.split('\n').filter(line => line.trim() !== '').join('\n');
  };

  const performSingleSpacing = (text: string, opts: RemovalOptions): string => {
    return text.replace(/ {2,}/g, ' ');
  };

  const performTabsToSpaces = (text: string, opts: RemovalOptions): string => {
    return text.replace(/\t/g, ' '.repeat(opts.spacesPerTab));
  };

  const performSpacesToTabs = (text: string, opts: RemovalOptions): string => {
    const spacePattern = new RegExp(' '.repeat(opts.spacesPerTab), 'g');
    return text.replace(spacePattern, '\t');
  };

  const performCompleteRemoval = (text: string, opts: RemovalOptions): string => {
    if (opts.replaceWhitespaceWith) {
      return text.replace(/\s/g, opts.replaceWhitespaceWith);
    }
    return text.replace(/\s/g, '');
  };

  const performCustomProcessing = (text: string, opts: RemovalOptions): string => {
    let result = text;

    // Normalize line endings
    if (opts.normalizeLineEndings) {
      result = result.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    }

    // Convert tabs to spaces
    if (opts.convertTabsToSpaces) {
      result = result.replace(/\t/g, ' '.repeat(opts.spacesPerTab));
    }

    // Process line by line
    const lines = result.split('\n');
    let processedLines = lines.map(line => {
      let processedLine = line;

      // Trim each line
      if (opts.trimEachLine) {
        processedLine = processedLine.trim();
      } else {
        // Remove leading whitespace
        if (opts.removeLeadingWhitespace) {
          processedLine = processedLine.trimStart();
        }
        
        // Remove trailing whitespace
        if (opts.removeTrailingWhitespace) {
          processedLine = processedLine.trimEnd();
        }
      }

      // Remove extra spaces
      if (opts.removeExtraSpaces) {
        if (opts.preserveIndentation) {
          const leadingSpaces = line.match(/^ */)?.[0] || '';
          const content = processedLine.trimStart();
          const cleanContent = content.replace(/ {2,}/g, ' ');
          processedLine = leadingSpaces + cleanContent;
        } else {
          processedLine = processedLine.replace(/ {2,}/g, ' ');
        }
      }

      return processedLine;
    });

    // Remove empty lines
    if (opts.removeEmptyLines) {
      processedLines = processedLines.filter(line => line.trim() !== '');
    }

    // Filter by line length
    processedLines = processedLines.filter(line => 
      line.length >= opts.minimumLineLength && line.length <= opts.maximumLineLength
    );

    // Remove all whitespace
    if (opts.removeAllWhitespace) {
      if (opts.replaceWhitespaceWith) {
        return processedLines.join('\n').replace(/\s/g, opts.replaceWhitespaceWith);
      }
      return processedLines.join('\n').replace(/\s/g, '');
    }

    return processedLines.join('\n');
  };

  const calculateStatistics = (original: string, processed: string): WhitespaceStats => {
    const originalLength = original.length;
    const processedLength = processed.length;
    const charactersRemoved = originalLength - processedLength;
    
    // Count whitespace types in original
    const whitespaceTypes = {
      spaces: (original.match(/ /g) || []).length,
      tabs: (original.match(/\t/g) || []).length,
      newlines: (original.match(/\n/g) || []).length,
      carriageReturns: (original.match(/\r/g) || []).length,
      formFeeds: (original.match(/\f/g) || []).length,
      verticalTabs: (original.match(/\v/g) || []).length,
    };

    const processedWhitespace = {
      spaces: (processed.match(/ /g) || []).length,
      tabs: (processed.match(/\t/g) || []).length,
      newlines: (processed.match(/\n/g) || []).length,
    };

    const spacesRemoved = whitespaceTypes.spaces - processedWhitespace.spaces;
    const tabsRemoved = whitespaceTypes.tabs - processedWhitespace.tabs;
    const newlinesRemoved = whitespaceTypes.newlines - processedWhitespace.newlines;

    const originalLines = original.split('\n').length;
    const processedLines = processed.split('\n').length;
    const emptyLinesRemoved = originalLines - processedLines;

    const reductionPercentage = originalLength > 0 ? Math.round((charactersRemoved / originalLength) * 100) : 0;

    return {
      originalLength,
      processedLength,
      charactersRemoved,
      spacesRemoved,
      tabsRemoved,
      newlinesRemoved,
      originalLines,
      processedLines,
      emptyLinesRemoved,
      reductionPercentage,
      whitespaceTypes
    };
  };

  const createEmptyStats = (): WhitespaceStats => ({
    originalLength: 0,
    processedLength: 0,
    charactersRemoved: 0,
    spacesRemoved: 0,
    tabsRemoved: 0,
    newlinesRemoved: 0,
    originalLines: 0,
    processedLines: 0,
    emptyLinesRemoved: 0,
    reductionPercentage: 0,
    whitespaceTypes: {
      spaces: 0,
      tabs: 0,
      newlines: 0,
      carriageReturns: 0,
      formFeeds: 0,
      verticalTabs: 0,
    }
  });

  const removalResult: RemovalResult = useMemo(() => {
    return processWhitespace(inputText, selectedMethod, options);
  }, [inputText, selectedMethod, options]);

  useEffect(() => {
    setOutputText(removalResult.processedText);
  }, [removalResult]);

  const copyToClipboard = async () => {
    if (outputText) {
      await navigator.clipboard.writeText(outputText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const downloadResult = () => {
    if (outputText) {
      const summary = `Whitespace Removal Results:
Method: ${removalMethods.find(m => m.id === selectedMethod)?.name}
Original Length: ${removalResult.statistics.originalLength}
Processed Length: ${removalResult.statistics.processedLength}
Characters Removed: ${removalResult.statistics.charactersRemoved}
Reduction: ${removalResult.statistics.reductionPercentage}%

Processed Text:
${outputText}`;

      const blob = new Blob([summary], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'whitespace-cleaned.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const generateSampleText = () => {
    const samples = [
      `   Hello World!   \n\n  This    is    a    sample   text.  \n\t\tWith   extra   whitespace   \n\n\n   And   trailing   spaces   \n`,
      `function example() {\n    let x =    1;   \n    let y =     2;    \n\n    return x    +    y;\n}\n\n`,
      `Line 1   \n   Line 2\n\n\nLine 3    \n\t\tIndented line\n        Another indented line   \n`,
      `This   text   has   \n   multiple   \n\n   spaces   and   \n\n\n   empty   lines   \n\n`,
      `\t\tTabbed content\n    Mixed    indentation   \n\t    More    tabs   and   spaces\n\n\n`,
      `   Start   with   spaces   \nMiddle   content\n   End   with   spaces   \n\n\n\n`,
    ];
    setInputText(samples[Math.floor(Math.random() * samples.length)]);
  };

  const getColorClasses = (color: string) => {
    const colorMap = {
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      red: 'bg-red-50 border-red-200 text-red-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
      pink: 'bg-pink-50 border-pink-200 text-pink-800',
      orange: 'bg-orange-50 border-orange-200 text-orange-800',
      teal: 'bg-teal-50 border-teal-200 text-teal-800',
      cyan: 'bg-cyan-50 border-cyan-200 text-cyan-800',
      emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
      violet: 'bg-violet-50 border-violet-200 text-violet-800'
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-100 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-3">
            <Eraser className="text-red-600" />
            Advanced Whitespace Remover
          </h1>
          <p className="text-gray-600">Clean and optimize text by removing unwanted whitespace</p>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-700">Whitespace Processing</h2>
            <div className="flex gap-2">
              <button
                onClick={generateSampleText}
                className="flex items-center gap-2 px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm"
              >
                <Shuffle size={16} />
                Sample Text
              </button>
              <button
                onClick={() => setShowOptions(!showOptions)}
                className="flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm"
              >
                <Settings size={16} />
                Options
              </button>
              <button
                onClick={() => setShowStats(!showStats)}
                className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
              >
                <Eye size={16} />
                Statistics
              </button>
            </div>
          </div>

          <div className="text-center">
            <div className="text-sm text-gray-600 mb-2">
              Method: <span className="font-semibold">{removalMethods.find(m => m.id === selectedMethod)?.name}</span>
            </div>
            <div className="text-sm text-gray-600">
              Reduction: <span className="font-semibold text-red-600">{removalResult.statistics.reductionPercentage}%</span> 
              ({removalResult.statistics.charactersRemoved} characters removed)
            </div>
          </div>
        </div>

        {/* Processing Options */}
        {showOptions && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Settings className="text-purple-600" />
              <h2 className="text-xl font-semibold text-gray-800">Processing Options</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Basic Options</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.removeLeadingWhitespace}
                    onChange={(e) => setOptions({...options, removeLeadingWhitespace: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Remove Leading Whitespace</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.removeTrailingWhitespace}
                    onChange={(e) => setOptions({...options, removeTrailingWhitespace: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Remove Trailing Whitespace</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.removeExtraSpaces}
                    onChange={(e) => setOptions({...options, removeExtraSpaces: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Remove Extra Spaces</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.removeEmptyLines}
                    onChange={(e) => setOptions({...options, removeEmptyLines: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Remove Empty Lines</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.trimEachLine}
                    onChange={(e) => setOptions({...options, trimEachLine: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Trim Each Line</span>
                </label>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Advanced Options</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.preserveLineBreaks}
                    onChange={(e) => setOptions({...options, preserveLineBreaks: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Preserve Line Breaks</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.preserveIndentation}
                    onChange={(e) => setOptions({...options, preserveIndentation: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Preserve Indentation</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.normalizeLineEndings}
                    onChange={(e) => setOptions({...options, normalizeLineEndings: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Normalize Line Endings</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.convertTabsToSpaces}
                    onChange={(e) => setOptions({...options, convertTabsToSpaces: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Convert Tabs to Spaces</span>
                </label>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Spaces per Tab</label>
                  <input
                    type="number"
                    min="1"
                    max="8"
                    value={options.spacesPerTab}
                    onChange={(e) => setOptions({...options, spacesPerTab: parseInt(e.target.value) || 4})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Special Options</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.removeAllWhitespace}
                    onChange={(e) => setOptions({...options, removeAllWhitespace: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Remove All Whitespace</span>
                </label>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Replace Whitespace With</label>
                  <input
                    type="text"
                    value={options.replaceWhitespaceWith}
                    onChange={(e) => setOptions({...options, replaceWhitespaceWith: e.target.value})}
                    placeholder="Replacement character..."
                    disabled={!options.removeAllWhitespace}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm disabled:bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Min Line Length</label>
                  <input
                    type="number"
                    min="0"
                    value={options.minimumLineLength}
                    onChange={(e) => setOptions({...options, minimumLineLength: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Max Line Length</label>
                  <input
                    type="number"
                    min="1"
                    value={options.maximumLineLength}
                    onChange={(e) => setOptions({...options, maximumLineLength: parseInt(e.target.value) || 1000})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Input and Output */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-lg font-semibold text-gray-700">Original Text</label>
                <div className="text-sm text-gray-500">
                  {removalResult.statistics.originalLength} chars, {removalResult.statistics.originalLines} lines
                </div>
              </div>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Enter text with whitespace to clean..."
                className="w-full h-64 p-4 border-2 border-gray-200 rounded-xl focus:border-red-500 focus:outline-none resize-none text-gray-700 transition-colors font-mono text-sm whitespace-pre"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-lg font-semibold text-gray-700">Cleaned Text</label>
                <div className="flex items-center gap-2">
                  <div className="text-sm text-gray-500">
                    {removalResult.statistics.processedLength} chars, {removalResult.statistics.processedLines} lines
                  </div>
                  <button
                    onClick={copyToClipboard}
                    disabled={!outputText}
                    className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm disabled:opacity-50"
                  >
                    <Copy size={16} />
                    {copySuccess ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={downloadResult}
                    disabled={!outputText}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm disabled:opacity-50"
                  >
                    <Download size={16} />
                    Download
                  </button>
                </div>
              </div>
              <textarea
                value={outputText}
                readOnly
                className="w-full h-64 p-4 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-700 resize-none font-mono text-sm whitespace-pre"
                placeholder="Cleaned text will appear here..."
              />
            </div>
          </div>
        </div>

        {/* Removal Methods */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Eraser className="text-red-600" />
            <h2 className="text-xl font-semibold text-gray-800">Choose Removal Method</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {removalMethods.map((method) => (
              <div
                key={method.id}
                onClick={() => setSelectedMethod(method.id)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                  selectedMethod === method.id
                    ? `border-${method.color}-500 ${getColorClasses(method.color)} shadow-md`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {method.icon}
                    <h3 className={`font-semibold ${
                      selectedMethod === method.id ? '' : 'text-gray-800'
                    }`}>
                      {method.name}
                    </h3>
                  </div>
                  {selectedMethod === method.id && (
                    <div className={`w-3 h-3 bg-${method.color}-500 rounded-full`}></div>
                  )}
                </div>
                <p className={`text-sm ${
                  selectedMethod === method.id ? '' : 'text-gray-600'
                }`}>
                  {method.description}
                </p>
              </div>
            ))}
          </div>

          {/* Method Help */}
          <div className="mt-6 p-4 bg-gray-50 rounded-xl">
            <h3 className="font-semibold text-gray-700 mb-2">
              {removalMethods.find(m => m.id === selectedMethod)?.name} Help
            </h3>
            <div className="text-sm text-gray-600">
              {selectedMethod === 'basic' && (
                <div>
                  <p>• Removes trailing whitespace from each line</p>
                  <p>• Converts multiple spaces to single spaces</p>
                  <p>• Removes empty lines at the end</p>
                </div>
              )}
              {selectedMethod === 'aggressive' && (
                <div>
                  <p>• Trims all lines completely</p>
                  <p>• Removes all empty lines</p>
                  <p>• Converts all whitespace sequences to single spaces</p>
                </div>
              )}
              {selectedMethod === 'minimal' && (
                <div>
                  <p>• Only removes trailing spaces from lines</p>
                  <p>• Preserves all other whitespace structure</p>
                  <p>• Gentle cleanup for sensitive formatting</p>
                </div>
              )}
              {selectedMethod === 'normalize' && (
                <div>
                  <p>• Standardizes line endings to LF</p>
                  <p>• Converts tabs to spaces</p>
                  <p>• Normalizes spacing while preserving indentation</p>
                </div>
              )}
              {selectedMethod === 'compact' && (
                <div>
                  <p>• Maximum compression while maintaining readability</p>
                  <p>• Removes empty lines and trims all content</p>
                  <p>• Ideal for reducing file size</p>
                </div>
              )}
              {selectedMethod === 'trimLines' && (
                <div>
                  <p>• Removes leading and trailing spaces from each line</p>
                  <p>• Preserves line breaks and empty lines</p>
                  <p>• Good for cleaning up indentation issues</p>
                </div>
              )}
              {selectedMethod === 'removeEmpty' && (
                <div>
                  <p>• Removes all blank lines from text</p>
                  <p>• Preserves all other whitespace</p>
                  <p>• Useful for compacting text blocks</p>
                </div>
              )}
              {selectedMethod === 'singleSpace' && (
                <div>
                  <p>• Replaces multiple consecutive spaces with one</p>
                  <p>• Preserves tabs and line breaks</p>
                  <p>• Cleans up spacing inconsistencies</p>
                </div>
              )}
              {selectedMethod === 'tabsToSpaces' && (
                <div>
                  <p>• Converts all tab characters to spaces</p>
                  <p>• Uses configurable spaces per tab setting</p>
                  <p>• Standardizes indentation</p>
                </div>
              )}
              {selectedMethod === 'spacesToTabs' && (
                <div>
                  <p>• Converts space indentation to tabs</p>
                  <p>• Uses configurable spaces per tab ratio</p>
                  <p>• Reduces file size for indented content</p>
                </div>
              )}
              {selectedMethod === 'removeAll' && (
                <div>
                  <p>• Removes every whitespace character</p>
                  <p>• Option to replace with custom character</p>
                  <p>• Creates completely compact text</p>
                </div>
              )}
              {selectedMethod === 'custom' && (
                <div>
                  <p>• Uses your custom configuration options</p>
                  <p>• Combines multiple processing techniques</p>
                  <p>• Fine-grained control over whitespace handling</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Statistics */}
        {showStats && inputText && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Eye className="text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-800">Whitespace Statistics</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Reduction</h3>
                <div className="text-2xl font-bold text-blue-900">{removalResult.statistics.reductionPercentage}%</div>
                <p className="text-sm text-blue-600">Size reduction</p>
              </div>

              <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                <h3 className="font-semibold text-red-800 mb-2">Characters Removed</h3>
                <div className="text-2xl font-bold text-red-900">{removalResult.statistics.charactersRemoved}</div>
                <p className="text-sm text-red-600">Total removed</p>
              </div>

              <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">Final Length</h3>
                <div className="text-2xl font-bold text-green-900">{removalResult.statistics.processedLength}</div>
                <p className="text-sm text-green-600">Characters remaining</p>
              </div>

              <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                <h3 className="font-semibold text-orange-800 mb-2">Lines</h3>
                <div className="text-2xl font-bold text-orange-900">{removalResult.statistics.processedLines}</div>
                <p className="text-sm text-orange-600">Final line count</p>
              </div>
            </div>

            {/* Detailed Whitespace Analysis */}
            <div className="mt-6">
              <h3 className="font-semibold text-gray-700 mb-4">Whitespace Type Analysis</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-2">Spaces</h4>
                  <div className="text-sm space-y-1">
                    <div>Original: <span className="font-bold">{removalResult.statistics.whitespaceTypes.spaces}</span></div>
                    <div>Removed: <span className="font-bold text-red-600">{removalResult.statistics.spacesRemoved}</span></div>
                  </div>
                </div>

                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-2">Tabs</h4>
                  <div className="text-sm space-y-1">
                    <div>Original: <span className="font-bold">{removalResult.statistics.whitespaceTypes.tabs}</span></div>
                    <div>Removed: <span className="font-bold text-red-600">{removalResult.statistics.tabsRemoved}</span></div>
                  </div>
                </div>

                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-2">Line Breaks</h4>
                  <div className="text-sm space-y-1">
                    <div>Original: <span className="font-bold">{removalResult.statistics.whitespaceTypes.newlines}</span></div>
                    <div>Removed: <span className="font-bold text-red-600">{removalResult.statistics.newlinesRemoved}</span></div>
                  </div>
                </div>

                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-2">Carriage Returns</h4>
                  <div className="text-sm">
                    <div>Found: <span className="font-bold">{removalResult.statistics.whitespaceTypes.carriageReturns}</span></div>
                  </div>
                </div>

                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-2">Form Feeds</h4>
                  <div className="text-sm">
                    <div>Found: <span className="font-bold">{removalResult.statistics.whitespaceTypes.formFeeds}</span></div>
                  </div>
                </div>

                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-2">Vertical Tabs</h4>
                  <div className="text-sm">
                    <div>Found: <span className="font-bold">{removalResult.statistics.whitespaceTypes.verticalTabs}</span></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Before/After Comparison */}
            <div className="mt-6">
              <h3 className="font-semibold text-gray-700 mb-3">Before vs After Comparison:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="font-semibold text-red-800 mb-1">Original Text</h4>
                  <div className="text-sm text-red-700">
                    <div>{removalResult.statistics.originalLength} characters</div>
                    <div>{removalResult.statistics.originalLines} lines</div>
                  </div>
                </div>
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-1">Processed Text</h4>
                  <div className="text-sm text-green-700">
                    <div>{removalResult.statistics.processedLength} characters</div>
                    <div>{removalResult.statistics.processedLines} lines</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Preview Section */}
        {inputText && (
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <Hash className="text-purple-600" />
              <h2 className="text-xl font-semibold text-gray-800">Processing Preview</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">Original Text Sample (first 200 chars)</h3>
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg font-mono text-sm whitespace-pre-wrap break-all max-h-32 overflow-auto">
                  {inputText.substring(0, 200)}{inputText.length > 200 ? '...' : ''}
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-700 mb-2">Processed Text Sample (first 200 chars)</h3>
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg font-mono text-sm whitespace-pre-wrap break-all max-h-32 overflow-auto">
                  {outputText.substring(0, 200)}{outputText.length > 200 ? '...' : ''}
                </div>
              </div>
            </div>

            {/* Processing Summary */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-semibold text-blue-700 mb-2">Processing Summary</h3>
              <div className="text-sm text-blue-800 space-y-1">
                <div>Method: <span className="font-semibold">{removalMethods.find(m => m.id === selectedMethod)?.name}</span></div>
                <div>Reduction: <span className="font-semibold">{removalResult.statistics.charactersRemoved} characters ({removalResult.statistics.reductionPercentage}%)</span></div>
                <div>Lines: <span className="font-semibold">{removalResult.statistics.originalLines} → {removalResult.statistics.processedLines}</span></div>
                {removalResult.statistics.emptyLinesRemoved > 0 && (
                  <div>Empty lines removed: <span className="font-semibold">{removalResult.statistics.emptyLinesRemoved}</span></div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhitespaceRemover;